import { useEffect, useState } from "react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Backdrop,
  useTheme,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import { openSnackbar } from "api/snackbar";
import { SnackbarProps } from "../../../../../types/snackbar";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Formik, Form, Field } from "formik";
import ImageUploader from "components/ImageUploader";
import { Category } from "../../../../../types/Category";
import { Article } from "../../../../../types/article";
import { mockArticleData } from "./mockData";

const AddEditArticle = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [articleData, setArticleData] = useState<Article>(mockArticleData);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchArticle();
    }
  }, [id]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];

    // Check if a file was selected
    if (file) {
      // Check if the selected file is an image
      if (file.type.startsWith("image/")) {
        setFieldValue("image", file);
        // Create preview URL
        const fileUrl = URL.createObjectURL(file);
        setPreviewUrl(fileUrl);
      } else {
        setFieldValue("image", null);
        setPreviewUrl(null);
      }
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await USERAXIOS.get("/category?page=1?parent_id=10");
      setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      openSnackbar({
        open: true,
        message: "Error fetching categories",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await USERAXIOS.get(`/blogs/${id}`);
      const { title, short_text, text, status, category_id, lang, image } =
        response.data.data;
      setArticleData({
        title,
        short_text,
        text,
        status: status ? 1 : 0,
        category_id,
        lang,
        image: null,
      });
      if (image) {
        setPreviewUrl(image);
      }
    } catch (error) {
      console.error("Error fetching article:", error);
      openSnackbar({
        open: true,
        message: "Error fetching article",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    values: Article,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const formData = new FormData();

      formData.append("user_id", "3");
      formData.append("title", values.title);
      formData.append("short_text", values.short_text);
      formData.append("text", values.text);
      formData.append("status", values.status.toString());
      formData.append("category_id", values.category_id.toString());
      formData.append("lang", values.lang);

      // Only append image if it's a new file (starts with blob:)
      if (values.image && previewUrl?.startsWith("blob:")) {
        formData.append("image", values.image);
      }

      if (id) {
        await USERAXIOS.post(`/blogs/${id}`, formData);
      } else {
        await USERAXIOS.post("/blogs", formData);
      }

      openSnackbar({
        open: true,
        message: `Article ${id ? "updated" : "created"} successfully`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);

      navigate("/articles-list");
    } catch (error) {
      console.error("Error saving article:", error);
      openSnackbar({
        open: true,
        message: `Error ${id ? "updating" : "creating"} article`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setSubmitting(false);
    }
  };

  // Add a specific handler for ReactQuill
  const handleQuillChange = (content: string) => {
    setArticleData((prev) => ({
      ...prev,
      text: content,
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "primary",
        }}
        open={loading}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
        </Stack>
      </Backdrop>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              <Typography variant="h4" color="primary">
                <FormattedMessage id={id ? "Edit Article" : "Add Article"} />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Formik
                initialValues={articleData}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isSubmitting, setFieldValue }) => (
                  <Form>
                    <Divider style={{ marginBottom: "40px" }} />

                    <Stack spacing={3}>
                      <Typography>
                        <FormattedMessage id="Title" />{" "}
                      </Typography>
                      <Field name="title">
                        {({ field }: { field: any }) => (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder="Title"
                            required
                          />
                        )}
                      </Field>
                      <Typography>
                        <FormattedMessage id="Short Text" />{" "}
                      </Typography>
                      <Field name="short_text">
                        {({ field }: { field: any }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            required
                          />
                        )}
                      </Field>

                      <Stack spacing={1}>
                        <InputLabel htmlFor="text">
                          <FormattedMessage id="Content" />
                        </InputLabel>
                        <Field name="text">
                          {({ field }: { field: any }) => (
                            <ReactQuill
                              style={{ height: "150px", marginBottom: "50px" }}
                              theme="snow"
                              value={field.value}
                              onChange={(content) =>
                                field.onChange({
                                  target: { name: "text", value: content },
                                })
                              }
                            />
                          )}
                        </Field>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                          <FormControl fullWidth>
                            <Field name="category_id">
                              {({ field }: { field: any }) => (
                                <Select
                                  {...field}
                                  displayEmpty
                                  renderValue={(selected) => {
                                    if (!selected) {
                                      return (
                                        <FormattedMessage id="Course Category" />
                                      );
                                    }

                                    const selectedOption = categories.find(
                                      (opt) => opt.id === selected
                                    );
                                    if (!selectedOption) return null;

                                    return theme.direction === "rtl"
                                      ? selectedOption.name_trans.ar
                                      : selectedOption.name;
                                  }}
                                >
                                  {loadingCategories ? (
                                    <MenuItem disabled>
                                      <CircularProgress size={20} />
                                    </MenuItem>
                                  ) : (
                                    categories.map((category) => (
                                      <MenuItem
                                        key={category.id}
                                        value={category.id}
                                      >
                                        {theme.direction === "rtl"
                                          ? category.name_trans.ar
                                          : category.name}
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              )}
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <Stack spacing={1}>
                            <FormControl fullWidth>
                              <Field as={Select} name="lang">
                                <MenuItem value="EN">
                                  <FormattedMessage id="English" />
                                </MenuItem>
                                <MenuItem value="AR">
                                  <FormattedMessage id="Arabic" />
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Stack>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography>
                          <FormattedMessage id="Article Image" />
                        </Typography>
                        <ImageUploader
                          id="article-image-upload"
                          previewUrl={previewUrl}
                          onChange={handleFileChange}
                          setFieldValue={setFieldValue}
                          label="Click to upload article image"
                        />
                      </Grid>
                      <Box>
                        <Field name="status">
                          {({ field }: { field: any }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={field.value === 1}
                                  onChange={(e) =>
                                    field.onChange({
                                      target: {
                                        name: "status",
                                        value: e.target.checked ? 1 : 0,
                                      },
                                    })
                                  }
                                />
                              }
                              label="status"
                              // label={field.value === 1 ? "1" : "0"}
                            />
                          )}
                        </Field>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        marginTop="4rem"
                      >
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                            <Grid item md={2} />
                            <Grid item md={4}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/articles-list")}
                              >
                                <FormattedMessage id="Cancel" />
                              </Button>
                            </Grid>
                            <Grid item md={4}>
                              <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <CircularProgress
                                    size="1.5rem"
                                    style={{ color: "white" }}
                                  />
                                ) : (
                                  <FormattedMessage
                                    id={id ? "Update" : "Create"}
                                  />
                                )}
                              </Button>
                            </Grid>
                            <Grid item md={2} />
                          </Stack>
                        </Grid>
                      </Stack>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddEditArticle;
